import { jwtDecode, JwtPayload } from "jwt-decode";
import { Preferences } from "@capacitor/preferences";
import { Capacitor } from "@capacitor/core";

export function isNative() {
  return Capacitor.isNativePlatform();
}

export function isInsideCapacitorBrowser() {
  return (
    isNative() &&
    // Seems like the Capacitor In-App Browser does not set the referrer
    document.referrer === "" &&
    window.Capacitor &&
    window.Capacitor?.Plugins.Browser !== undefined
  );
}

export async function persistToken(key: string, value: string) {
  await Preferences.set({ key, value });
}

export async function getToken(key: string) {
  const result = await Preferences.get({ key });
  return result.value;
}

export async function deleteToken(key: string) {
  await Preferences.remove({ key });
}

/**
 * Get persisted token from storage and check if it is expired.
 * If it is expired, it deletes the token from storage.
 *
 * @param storageKey - The key to use to get the token from storage.
 * @returns The token if it is not expired, otherwise undefined.
 */
export async function getPersistedToken(storageKey: string) {
  const persistedToken = await getToken(storageKey);

  if (!persistedToken) {
    return;
  }

  const parsedToken = parseToken(persistedToken);

  if (isExpired(parsedToken)) {
    deleteToken(storageKey);
    return;
  }

  return {
    parsed: parsedToken,
    raw: persistedToken,
  };
}

export function isPersistedTokenValid(token: string) {
  if (!token) return false;

  const parsedToken = parseToken(token);
  return !isExpired(parsedToken);
}

export function parseToken(token: string) {
  try {
    return jwtDecode(token);
  } catch (error) {
    throw new Error("Could not parse token");
  }
}

function isExpired(token: JwtPayload) {
  if (!token) return true;
  if (!token.exp) return false;

  return Date.now() >= token.exp * 1000;
}

/**
 * Get the expiration date of a token. If the token does not have an expiration date,
 * it will return a date far in the future.
 *
 * @param token - The token to get the expiration date from.
 * @returns - The expiration date of the token.
 */
export function getExpirationDate(token: JwtPayload) {
  if (isExpired(token)) return;

  const expirationDate = token.exp
    ? new Date(token.exp * 1000)
    : new Date("2999-12-31");
  expirationDate.setSeconds(expirationDate.getSeconds() - 10);

  return expirationDate;
}
